/**
 * profile.js - where this child starts, in this game.
 *
 * PURE. PLATFORM SCAFFOLD.
 *
 * ---------------------------------------------------------------------------
 * THE CHART TRACKS THE CHILD, NOT THE GAME
 *
 * Section 9.1. A game never keeps its own notion of "what level am I on": it
 * reads the CHILD's phase and level, declares what it covers, and asks the
 * engine (section 9.2). The answer already accounts for the coverage, so no
 * caller re-clamps it.
 *
 * ---------------------------------------------------------------------------
 * ONE SPELLING, IN ONE PLACE
 *
 * `GET /api/children` returns `currentPhase` / `currentLevel`. The column is
 * `current_phase` / `current_level`, and reading a child straight out of a
 * query gives you those instead. A game that reads the wrong one gets
 * `undefined`, falls back to phase 1 level 1, and places EVERY child at the
 * start of the chart - which looks exactly like a game working, and quietly
 * undoes cross-game placement, the thing section 9.3 depends on. Both spellings
 * are read here so no game has to know which it was handed.
 */

import { placeInGame } from '../../../engine/progression.js';

/**
 * @param {object|null} child  a row from the children API, or null for practice
 * @returns {{currentPhase:number, currentLevel:number, overallColor:string}}
 */
export function readPosition(child) {
  const pick = (camel, snake, fallback) => {
    const value = child ? (child[camel] ?? child[snake]) : undefined;
    return value === undefined || value === null ? fallback : value;
  };
  return {
    currentPhase: pick('currentPhase', 'current_phase', 1),
    currentLevel: pick('currentLevel', 'current_level', 1),
    // Section 9.2 scales the extra support a child is placed with to their
    // colour. An unassessed child is gray, which carries the largest offset.
    overallColor: pick('overallColor', 'overall_color', 'gray'),
  };
}

/**
 * The engine's placement (section 9.2) for this child in this game.
 *
 * @param {object|null} child
 * @param {{id:string, phasesAvailable:number[], levelsAvailable:Array}} game
 * @returns {object} placeInGame()'s result, unmodified. `clamped`, `placement`
 *          and `warning` already say whether the child was moved to fit.
 */
export function placeChildInGame(child, game) {
  const { currentPhase, currentLevel, overallColor } = readPosition(child);
  return placeInGame({
    currentPhase,
    currentLevel,
    overallColor,
    game: {
      id: game.id,
      phasesAvailable: game.phasesAvailable,
      levelsAvailable: game.levelsAvailable,
    },
  });
}

export default { readPosition, placeChildInGame };
